// ============================================================================
// Usage Visibility — pure gate logic (ORG-USAGE-2026)
//
// Decides whether a viewer may see model/token usage detail (the per-message
// info popup), the Wallet page, and the Activity page.
//
// ── W5 stage 3 (R7, 2026-08-13): the grandfather clause is GONE ─────────────
//
// This gate used to fail open for any account without a
// `usageVisibilityGeneration` marker, because shipping the feature could not be
// allowed to take anything away from anyone while un-upgraded accounts existed.
// That population is now empty: the stage-2 batch upgrade drove the fleet to
// 166/166 governed, 0 ungoverned, 0 partial, 0 accounts without an RBAC record
// (verified by scripts/audit-usage-visibility-governance.mjs). New accounts are
// governed at creation (founderBootstrap, stage 1), so the set cannot refill.
//
// With no legitimate ungoverned account left, an ABSENT marker no longer means
// "legacy, be generous" — it means something is wrong, and being generous there
// is exactly the hole R7 ("role-based restrictions always enforced") forbids.
// So: absent/low generation now resolves STRICT, and the member's own permission
// set decides. This is the deliberate fail-open → fail-closed flip, taken only
// after the population was migrated — flipping it earlier would have locked
// legitimate users out of their own wallets, which is why it was staged.
//
// STILL OPEN, deliberately — and these are transient states, not a policy
// fallback:
//   • `loading` — RBAC has not resolved yet. Restricting here would flash
//     "access restricted" on every page load before the answer arrives; that is
//     a race, not a permission decision.
//   • `error` — a Firestore/network blip must not lock an owner out of their
//     own billing page. The client gate is defense-in-depth; the sensitive
//     server-scoped surfaces (e.g. Activity's `activity.view-team` scoping)
//     enforce independently server-side.
// Both are documented residuals rather than silent behaviour; see the PR.
//
// (Contrast with the L4 GUEST gate in guestUsagePrivacy.tsx, which fails
// CLOSED on purpose: a signed-out visitor must never see compute detail. That
// gate runs first and is untouched here.)
// ============================================================================

/** Must match USAGE_VISIBILITY_GENERATION in functions/src/rbac/usageLimits.ts */
export const USAGE_VISIBILITY_GENERATION = 2;

export interface UsageVisibilityInput {
  loading: boolean;
  error: string | null;
  isOwner: boolean;
  hasPermission: (key: string) => boolean;
  /** From the member doc; null/absent = account predates the feature */
  usageVisibilityGeneration: number | null;
  /**
   * Is there an authenticated identity to enforce against?
   *
   * Distinguishes "RBAC resolved, this member lacks the key" (restrict) from
   * "no user / mounted outside AuthProvider" (nothing to enforce — fail open).
   * Defaults to true so existing callers that pass a resolved member keep
   * enforcing; useRbac supplies the real value.
   */
  hasIdentity?: boolean;
}

export interface UsageVisibility {
  /** Per-message info popup contents, personal usage meters */
  canSeeUsageDetail: boolean;
  /** Wallet page + nav entry */
  canSeeWallet: boolean;
  /** Activity page */
  canSeeActivity: boolean;
  /** Every member's activity, member drill-downs */
  canSeeTeamActivity: boolean;
  /**
   * Activity of members whose roles sit beneath the viewer's roles (R19).
   * Additive like team scope — it only ever appears for someone who holds it.
   * Team scope implies it (workspace-wide covers every subtree).
   */
  canSeeSubtreeActivity: boolean;
  /** Usage-limit configuration surfaces */
  canViewLimits: boolean;
  canManageLimits: boolean;
  canDelegateLimits: boolean;
  /**
   * Plan changes: upgrade/downgrade/commit/seat changes (2026-08-13 WU
   * ladder). ADDITIVE like team scope — billing.manage only ever appears for
   * someone who positively holds it (or the owner): defaulting plan-change
   * controls open would let any grandfathered member commit the org to an
   * annual contract.
   */
  canManageBilling: boolean;
  /**
   * The ORGANIZATION-WIDE spending limit + balance stop (2026-08-14 owner
   * directive). Distinct from canViewLimits/canManageLimits, which govern the
   * per-role and per-member usage limits. Additive: owners always, plus anyone
   * holding the pool-limit keys (admins hold them by default).
   */
  canViewPoolLimits: boolean;
  canManagePoolLimits: boolean;
  /** true once RBAC settled — gates are advisory (open) before this */
  ready: boolean;
  /** true when this account is governed by the new permission model */
  strict: boolean;
}

/**
 * `strict` is the switch between "grandfathered, show everything" and
 * "governed by the org's permission set".
 */
export function isStrictVisibilityAccount(generation: number | null | undefined): boolean {
  return typeof generation === 'number' && generation >= USAGE_VISIBILITY_GENERATION;
}

export function resolveUsageVisibility(input: UsageVisibilityInput): UsageVisibility {
  const { loading, error, isOwner, hasPermission, hasIdentity = true } = input;

  const ready = !loading && !error;
  // W5 stage 3: governance is assumed once RBAC has resolved for a REAL
  // identity. The generation marker is no longer consulted to WAIVE
  // enforcement — every account is governed (see the header).
  // `isStrictVisibilityAccount` is retained and still exported for callers that
  // report an account's migration state, but it no longer gates the decision.
  //
  // `hasIdentity` keeps the documented no-provider contract intact: with no
  // signed-in user (embedded host, isolated component test) there is no member
  // to enforce against, so the gate stays open and the signed-out L4 guest gate
  // — which fails CLOSED — remains the one that governs that case.
  const strict = ready && hasIdentity && !isOwner;

  // Enforced: once RBAC has resolved, the member's own permission set decides.
  // Transient states (loading / error) leave `strict` false and stay open.
  const allow = (key: string) => !strict || hasPermission(key);

  return {
    canSeeUsageDetail: isOwner || allow('usage.view-own'),
    canSeeWallet: isOwner || allow('billing.view'),
    canSeeActivity: isOwner || allow('activity.view'),
    // Team-wide scope is ADDITIVE, not grandfathered: it only ever appears for
    // someone who actually holds it (or the owner). Defaulting this open would
    // expose other members' usage to people who never had it.
    // usage.view ("View Usage" — workspace usage statistics) also opens the
    // workspace-wide scope: activity.view-team previously did double duty as
    // both "see team activity" and "see workspace-wide usage". Still additive —
    // both keys must be positively held.
    canSeeTeamActivity:
      isOwner ||
      (ready && (hasPermission('activity.view-team') || hasPermission('usage.view'))),
    // Subtree scope is additive for the same reason, and team implies it.
    canSeeSubtreeActivity:
      isOwner ||
      (ready &&
        (hasPermission('activity.view-team') || hasPermission('activity.view-subtree'))),
    canViewLimits:
      isOwner ||
      (ready && (hasPermission('usage.limits.view') || hasPermission('usage.limits.manage'))),
    canManageLimits: isOwner || (ready && hasPermission('usage.limits.manage')),
    canDelegateLimits: isOwner || (ready && hasPermission('usage.limits.delegate')),
    canManageBilling: isOwner || (ready && hasPermission('billing.manage')),
    canViewPoolLimits:
      isOwner ||
      (ready && (hasPermission('usage.pool-limits.view') || hasPermission('usage.pool-limits.manage'))),
    canManagePoolLimits: isOwner || (ready && hasPermission('usage.pool-limits.manage')),
    ready,
    strict,
  };
}
